import { Injectable } from '@angular/core';
import { AuthSessionService } from './auth/auth-session.service';
import { LogService } from 'src/app/baseServices/logging/log.service';
import { bytesToBase64 } from 'src/app/utils/converters/to-b64.util';
import { base64ToBytes } from 'src/app/utils/converters/to-bytes.util';

interface PasswordRecord {
  algo: string;
  iters: number;
  salt: string;
  hash: string;
  createdAt: number;
};


/**
 * Provides a slow password hashing mechanism on the client so the app can later verify the user re-enters the same password while offline.
 *
 * IMPORTANT: This is ONLY for local re-auth heuristics (e.g., unlocking a cached session).
 * Its stored hashes cannot be used to authenticate against the server.
 * AuthSessionService owns artifact persistence and retirement; this service owns hashing and comparison only.
 */
@Injectable({ providedIn: 'root' })
export class OfflinePasswordService {
  private static readonly ITERATIONS = 150_000;
  private static readonly SALT_LEN_BYTES = 16;
  private static readonly HASH_LEN_BITS = 256; // 32 bytes
  private static readonly ALGO_TAG = 'PBKDF2-SHA256';

  constructor(
    private log: LogService,
    private sessions: AuthSessionService
  ) {}

  /** Returns whether the active session has a hash; unavailable/initializing sessions must not be treated as hash-free kiosk accounts. */
  hasStoredHash(): boolean {
    const record = this.sessions.getOfflinePasswordRecord();
    if (this.sessions.state.phase !== 'authenticated') { throw new Error('Offline credential session unavailable'); }
    return !!record;
  }

  /** Removes stored record */
  clear(): void {
    void this.sessions.clearOfflinePassword().catch(error => this.log.error('OfflinePassword.ClearFailed', error));
  }

  /**
   * Hash and store the provided password, overwriting any previous record.
   */
  async hashAndStore(password: string, persist?: (record: string) => Promise<void>): Promise<void> {
    const identity = this.sessions.state.identity;
    if (!identity || !this.sessions.isAuthenticated()) { return; }
    if (!password) {
      await this.sessions.clearOfflinePassword(identity);
      return;
    }
    try {
      const salt = this.randomSalt();
      const hashBuf = await this.deriveKey(password, salt, OfflinePasswordService.ITERATIONS);
      const record = {
        algo: OfflinePasswordService.ALGO_TAG,
        iters: OfflinePasswordService.ITERATIONS,
        salt: bytesToBase64(salt),
        hash: bytesToBase64(new Uint8Array(hashBuf)),
        createdAt: Date.now()
      };
      if (persist) { await persist(JSON.stringify(record)); }
      else { await this.sessions.storeOfflinePassword(identity, JSON.stringify(record)); }
      this.log.info('OfflinePassword.Stored', { algo: record.algo, iterations: record.iters });
    } catch (err) {
      this.log.error('OfflinePassword.HashFailed', err as any);
    }
  }

  /** Verify a candidate password against the stored record */
  async verify(password: string): Promise<boolean> {
    if (!password) return false;
    const identity = this.sessions.state.identity;
    if (!identity) return false;
    const raw = this.sessions.getOfflinePasswordRecord(identity);
    if (!raw) return false;

    try {
      const record: PasswordRecord = JSON.parse(raw) as PasswordRecord;
      if (record.algo !== OfflinePasswordService.ALGO_TAG) {
        this.log.warn('OfflinePassword.UnsupportedAlgo', { algo: record.algo });
        return false;
      }
  const salt = base64ToBytes(record.salt);
  const expected = base64ToBytes(record.hash);
      const derivedBuf = await this.deriveKey(password, salt, record.iters);
      const ok = this.timingSafeEqual(expected, new Uint8Array(derivedBuf)) && this.sessions.getOfflinePasswordRecord(identity) === raw;
      this.log.info('OfflinePassword.Verify', { success: ok });
      return ok;
    } catch (err) {
      this.log.error('OfflinePassword.VerifyFailed', err as any);
      return false;
    }
  }

  private randomSalt(): Uint8Array {
  const salt = new Uint8Array(OfflinePasswordService.SALT_LEN_BYTES);
  crypto.getRandomValues(salt);
  return salt;
}


  private async deriveKey(password: string, salt: Uint8Array, iterations: number): Promise<ArrayBuffer> {
    const enc = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      enc.encode(password),
      { name: 'PBKDF2' },
      false,
      ['deriveBits']
    );
    const bits = await crypto.subtle.deriveBits(
      {
        name: 'PBKDF2',
        hash: 'SHA-256',
  salt: salt as BufferSource,
        iterations,
      },
      keyMaterial,
      OfflinePasswordService.HASH_LEN_BITS
    );
    return bits;
  }

  private timingSafeEqual(a: Uint8Array, b: Uint8Array): boolean {
    if (a.length !== b.length) return false;
    let diff = 0;
    for (let i = 0; i < a.length; i++) {
      diff |= a[i] ^ b[i];
    }
    return diff === 0;
  }
}
